/**
 * Currency experience, savings math, equivalent-cost display, and upsells.
 * All percentages and £/$ equivalents are derived from the v2 price book.
 */

import {
  formatMoneyFromMinor,
  parseRehubCurrency,
} from './currency';
import { formatMinorAmount, type CreditCurrency } from './credit-packs';
import {
  REHUB_CREDIT_MONTHLY_PLANS_V2,
  REHUB_CREDIT_PACKS_V2,
  REHUB_FREE_PLAN_V2,
  type RehubCreditSubscriptionDefinitionV2,
} from './credit-packs-v2';

export const TARGET_GENERATION_GROSS_MARGIN = 0.75;

export const CREDIT_POLICY_COPY_V2 = {
  hero: 'ONE ACCOUNT. TWO CREDIT TYPES.',
  supporting:
    'Use ReHub Credits across photo enhancement, virtual staging, animation, day-to-dusk and more. HDRPhoto uses a separate monthly HDR Credit allowance and never deducts ReHub Credits.',
  ecosystem: 'Use ReHub Credits across the ReHub AI property marketing ecosystem. HDRPhoto is billed in HDR Credits.',
  twoWallets:
    'ReHub Credits and HDR Credits are different wallets. ReHub Credits are shared across PhotoClear, PhotoEdit, Virtual Staging, HyperDusk, ImageMotion and other eligible tools. HDRPhoto uses HDR Credits — one HDR Credit per finished edit — and never spends ReHub Credits.',
  startFree: 'Start free with 10 ReHub Credits',
  startFreeCta: 'Start Free — 10 Credits',
  noCard: 'No card required.',
  lowBalance: "You're running low on credits.",
  buyCredits: 'Buy Credits',
  upgradePlan: 'Upgrade Plan',
  contactSales: 'Contact Sales',
  paygNeverExpire: 'Purchased credits never expire.',
  subscriptionRollover: 'Subscription credits roll over while your plan is active.',
  cancelKeep:
    'Cancel anytime. Unused subscription credits stay spendable for 12 months from the date they were granted. Purchased credits remain.',
  faqExpire:
    'Purchased PAYG ReHub Credits never expire. Subscription ReHub Credits roll over while your subscription is active, up to the plan cap. If you cancel, leftover subscription credits stay spendable for 12 months from the grant date. HDR Credits are a separate monthly allowance and reset with your HDRPhoto plan.',
} as const;

export function normalizeCurrency(raw: string | null | undefined): CreditCurrency | null {
  return parseRehubCurrency(raw);
}

/** Lowest effective paid credit value from the v2 price book (Starter monthly). */
export function lowestEffectivePaidCreditMinor(currency: CreditCurrency): number {
  let lowest = Number.POSITIVE_INFINITY;
  for (const plan of REHUB_CREDIT_MONTHLY_PLANS_V2) {
    const per = Math.round(plan.amountMinor[currency] / plan.credits);
    if (per < lowest) lowest = per;
  }
  for (const pack of REHUB_CREDIT_PACKS_V2) {
    const per = Math.round(pack.amountMinor[currency] / pack.credits);
    if (per < lowest) lowest = per;
  }
  return Number.isFinite(lowest) ? lowest : currency === 'GBP' ? 48 : currency === 'EUR' ? 58 : 63;
}

export function paygBaselinePerCreditMinor(currency: CreditCurrency): number {
  const pack20 = REHUB_CREDIT_PACKS_V2[0];
  return Math.round(pack20.amountMinor[currency] / pack20.credits);
}

export function subscriptionSavingsPercent(
  plan: RehubCreditSubscriptionDefinitionV2,
  currency: CreditCurrency
): number {
  const payg = paygBaselinePerCreditMinor(currency);
  const sub = plan.amountMinor[currency] / plan.credits;
  if (payg <= 0 || sub <= 0) return 0;
  return Math.max(0, Math.round((1 - sub / payg) * 100));
}

export function shouldShowEffectiveCashCost(opts: {
  isPaidPlan: boolean;
  usingPromotionalCredits?: boolean;
  usingRolledOverCredits?: boolean;
  balanceIsMostlyFree?: boolean;
}): boolean {
  if (!opts.isPaidPlan) return false;
  if (opts.usingPromotionalCredits || opts.usingRolledOverCredits || opts.balanceIsMostlyFree) {
    return false;
  }
  return true;
}

export function formatEffectiveCashCost(
  creditCost: number,
  currency: CreditCurrency,
  plan?: RehubCreditSubscriptionDefinitionV2 | null
): string {
  const perMinor = plan
    ? Math.round(plan.amountMinor[currency] / plan.credits)
    : lowestEffectivePaidCreditMinor(currency);
  const totalMinor = perMinor * creditCost;
  return `from ~${formatMinorAmount(totalMinor, currency)} equivalent`;
}

export function estimatedGrossMargin(opts: {
  creditCost: number;
  estimatedCostUsd: number;
}): { margin: number; belowTarget: boolean; revenueUsd: number } {
  const perCreditUsd = lowestEffectivePaidCreditMinor('USD') / 100;
  const revenueUsd = opts.creditCost * perCreditUsd;
  const margin = revenueUsd <= 0 ? 0 : (revenueUsd - opts.estimatedCostUsd) / revenueUsd;
  return {
    margin,
    belowTarget: margin < TARGET_GENERATION_GROSS_MARGIN,
    revenueUsd,
  };
}

export interface UpsellSuggestion {
  id: string;
  headline: string;
  body: string;
  cta: string;
  planId: 'pro_v3' | 'studio_v3';
}

export function getUpsellSuggestion(opts: {
  recentPaygCredits?: number;
  currentPlanId?: string | null;
  monthlyAllowance?: number | null;
  subscriptionCreditsUsedThisPeriod?: number | null;
}): UpsellSuggestion | null {
  const recentPayg = opts.recentPaygCredits || 0;
  const used = opts.subscriptionCreditsUsedThisPeriod || 0;
  const allowance = opts.monthlyAllowance || 0;
  const plan = opts.currentPlanId || 'free_v2';

  if ((plan === 'free_v2' || plan === 'starter_v2') && recentPayg >= 60) {
    return {
      id: 'payg_to_pro',
      headline: `You've purchased ${recentPayg} credits recently.`,
      body: 'ReHub Pro could give you 130 credits every month.',
      cta: 'See Pro',
      planId: 'pro_v3',
    };
  }

  if (plan === 'starter_v2' && allowance > 0 && used / allowance >= 0.8) {
    return {
      id: 'starter_approaching',
      headline: "You're getting a lot from ReHub.",
      body: 'Pro gives you 130 credits every month.',
      cta: 'Upgrade to Pro',
      planId: 'pro_v3',
    };
  }

  // Grandfathered pro_v2 (150/mo) subscribers see the current Studio plan.
  if ((plan === 'pro_v2' || plan === 'pro_v3') && allowance > 0 && used / allowance >= 0.85) {
    return {
      id: 'pro_approaching',
      headline: "You're using Pro hard.",
      body: 'Studio gives you 350 credits every month for agency-scale work.',
      cta: 'See Studio',
      planId: 'studio_v3',
    };
  }

  return null;
}

export function insufficientCreditsCopy(needed: number, balance: number): {
  title: string;
  body: string;
  balanceLabel: string;
} {
  return {
    title: `You need ${needed} credit${needed === 1 ? '' : 's'} to create this.`,
    body: CREDIT_POLICY_COPY_V2.lowBalance,
    balanceLabel: `Current balance: ${balance} credit${balance === 1 ? '' : 's'}`,
  };
}

export function lowBalanceThreshold(monthlyAllowance?: number | null): number {
  if (monthlyAllowance && monthlyAllowance > 0) return Math.max(5, Math.round(monthlyAllowance * 0.15));
  return 5;
}

export function isLowBalance(balance: number, monthlyAllowance?: number | null): boolean {
  return balance > 0 && balance <= lowBalanceThreshold(monthlyAllowance);
}

export function welcomeCreditsOnce(): number {
  return REHUB_FREE_PLAN_V2.credits;
}

export function formatPlanPrice(amountMinor: number, currency: CreditCurrency): string {
  return formatMoneyFromMinor(amountMinor, currency);
}
